import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { meApi } from '../api/me';
import { RoleType } from '../types';

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

const roleLabel = (role: RoleType): string => {
  if (role === 'ADMIN') return 'Administrator';
  if (role === 'EMPLOYEE') return 'Pracownik';
  return 'Klient';
};

const Profile: React.FC = () => {
  const { user, setUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  if (!user) return null;

  const onAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    setError('');
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Avatar musi być obrazem'); return; }
    if (file.size > MAX_AVATAR_BYTES) { setError('Avatar przekracza 5 MB'); return; }
    setUploading(true);
    try {
      const { data } = await meApi.uploadAvatar(file);
      setUser(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Nie udało się wgrać awatara');
    } finally {
      setUploading(false);
    }
  };

  const removeAvatar = async () => {
    if (!window.confirm('Usunąć awatar?')) return;
    setError('');
    setUploading(true);
    try {
      const { data } = await meApi.deleteAvatar();
      setUser(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Nie udało się usunąć awatara');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">Twoje konto</h1>

      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
        <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>Awatar</div>
        {error && <div className="auth-error">{error}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{
            width: 96, height: 96,
            borderRadius: '50%', overflow: 'hidden',
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface-alt)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.75rem', fontWeight: 600, color: 'var(--color-text-muted)',
          }}>
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="Avatar"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span>{user.firstName?.[0]}{user.lastName?.[0]}</span>
            )}
          </div>
          {/* Ta sama szerokość przycisków co w "Konto firmy" (logo) — spójny wygląd obu widoków. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
            <label className="btn btn-primary btn-sm" style={{ cursor: 'pointer', width: 160, textAlign: 'center' }}>
              {uploading ? 'Wysyłanie...' : (user.avatarUrl ? 'Zmień awatar' : 'Dodaj awatar')}
              <input type="file" accept="image/*" hidden disabled={uploading} onChange={onAvatarChange} />
            </label>
            {user.avatarUrl && (
              <button className="btn btn-danger btn-sm" style={{ width: 160 }} disabled={uploading} onClick={removeAvatar}>
                Usuń awatar
              </button>
            )}
            <span className="item-meta">PNG / JPG, max 5 MB</span>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: '1.25rem' }}>
        <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>Dane konta</div>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Imię i nazwisko</span>
            <span className="info-value">{user.firstName} {user.lastName}</span>
          </div>
          <div className="info-item">
            <span className="info-label">E-mail</span>
            <span className="info-value">{user.username}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Rola</span>
            <span className="info-value">{user.roles.map(roleLabel).join(', ')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
